import { useRef } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNewConfirmModal } from "modules/modals/hooks/useNewConfirmModal"
import { bulkUpdateTripsStatus, bulkDuplicateTrips } from "store/trips"
import { toaster } from "components/common/Toast"
import { loadingSelector } from "modules/loader-watchers/selectors"
import { EXPERIENCE_STATES } from "constants/experiences"

export const useBulkExperienceActions = ({ onComplete } = {}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { showConfirm, modal } = useNewConfirmModal()
  const loading = useSelector(loadingSelector("trips.trip"))
  const isExecutingRef = useRef(false)

  const run = async ({ records, actionType, thunk, success, onClearSelection }) => {
    if (isExecutingRef.current) return
    isExecutingRef.current = true
    try {
      await dispatch(thunk)
      toaster.success(success)
      onClearSelection?.()
      onComplete?.(records)
    } catch (err) {
      console.error(`bulk ${actionType} error:`, err)
      // Partial success — backend returns non-2xx but some records were updated.
      // Refetch so the updated ones are reflected in the list.
      if (err?.data?.data?.updated?.length > 0) {
        onClearSelection?.()
        onComplete?.(records)
      }
    } finally {
      isExecutingRef.current = false
    }
  }

  const confirmAndRun = ({ records, onClearSelection, actionType, title, message, confirmLabel, thunk, success }) => {
    if (!records?.length || isExecutingRef.current) return
    showConfirm(
      {
        title,
        message,
        confirmLabel,
        cancelLabel: t("global.cancel"),
        loading
      },
      () => run({ records, actionType, thunk, success, onClearSelection })
    )
  }

  const ids = (records) => records.map((record) => record.id)

  const handleBulkActivate = (records, onClearSelection) =>
    confirmAndRun({
      records,
      onClearSelection,
      actionType: "activate",
      title: t("experiences.bulk.activate.title", { count: records.length }),
      message: t("experiences.bulk.activate.message", { count: records.length }),
      confirmLabel: t("global.activate", { defaultValue: "Activate" }),
      thunk: bulkUpdateTripsStatus(ids(records), EXPERIENCE_STATES.published),
      success: t("experiences.bulk.activate.success", { count: records.length }),
    })

  const handleBulkDeactivate = (records, onClearSelection) =>
    confirmAndRun({
      records,
      onClearSelection,
      actionType: "deactivate",
      title: t("experiences.bulk.deactivate.title", { count: records.length }),
      message: t("experiences.bulk.deactivate.message", { count: records.length }),
      confirmLabel: t("global.deactivate", { defaultValue: "Deactivate" }),
      thunk: bulkUpdateTripsStatus(ids(records), EXPERIENCE_STATES.deactivated),
      success: t("experiences.bulk.deactivate.success", { count: records.length }),
    })

  const handleBulkDuplicate = (records, onClearSelection) =>
    confirmAndRun({
      records,
      onClearSelection,
      actionType: "duplicate",
      title: t("experiences.bulk.duplicate.title", { count: records.length }),
      message: t("experiences.bulk.duplicate.message", { count: records.length }),
      confirmLabel: t("global.duplicate", { defaultValue: "Duplicate" }),
      thunk: bulkDuplicateTrips(ids(records)),
      success: t("experiences.bulk.duplicate.success", { count: records.length }),
    })

  const handleBulkDelete = (records, onClearSelection) =>
    confirmAndRun({
      records,
      onClearSelection,
      actionType: "delete",
      title: t("experiences.bulk.delete.title", { count: records.length }),
      message: t("experiences.bulk.delete.message", { count: records.length }),
      confirmLabel: t("global.delete", { defaultValue: "Delete" }),
      thunk: bulkUpdateTripsStatus(ids(records), EXPERIENCE_STATES.archived),
      success: t("experiences.bulk.delete.success", { count: records.length }),
    })

  return { handleBulkActivate, handleBulkDeactivate, handleBulkDuplicate, handleBulkDelete, modal, isBulkLoading: loading }
}
